<template>
	<div class="ActivityBadge" :class="reverse ? 'ActivityBadge--reverse' : ''">
		<!-- opens modal for mobile -->
		<ul class="ActivityBadge-list u-hideForMedium">
			<li v-for="(badge, index) in badges" :key="index">
				<a
					class="ActivityBadge-badge"
					:class="badge.achieved ? 'ActivityBadge-badge--achieved' : ''"
					href="#Modal-activityBadge"
					@click.prevent="open(index)"
					ref="badges"
				>
					<div v-if="badge.loading" class="LoadingAnimation LoadingAnimation--small LoadingAnimation--basic">
						<CloIcon symbol="spinner" />
					</div>
					<CloIcon v-else :symbol="badge.icon" />
					<span class="ActivityBadge-label">{{ badge.label }}</span>
				</a>
			</li>
		</ul>
		<!-- links with tooltips for desktop -->
		<ul class="ActivityBadge-list u-showForMedium">
			<li v-for="(badge, index) in badges" :key="index">
				<a
					class="ActivityBadge-badge"
					:class="badge.achieved ? 'ActivityBadge-badge--achieved' : ''"
					:href="badge.href"
					:title="badge.text"
				>
					<CloLoadingAnimation v-if="badge.loading" class="LoadingAnimation LoadingAnimation--small LoadingAnimation--basic" />
					<CloIcon v-else :symbol="badge.icon" />
					<span class="ActivityBadge-label">{{ badge.label }}</span>
				</a>
				<span class="ActivityBadge-tooltip">
					{{ badge.text }}
				</span>
			</li>
		</ul>

		<!-- modal -->
		<transition name="slideUp">
			<div
				v-if="active"
				id="Modal-activityBadge"
				class="ActivityBadge-modal"
				tabindex="-1"
				role="dialog"
				ref="modal"
				@keyup.escape.stop.prevent="close"
			>
				<h1 class="Heading Heading--h3 u-stack-md">{{ title }}</h1>
				<div class="ActivityBadge ActivityBadge--vertical">
					<ul class="ActivityBadge-list">
						<li v-for="(badge, index) in badges" :key="index">
							<a
								class="ActivityBadge-badge"
								:class="badge.achieved ? 'ActivityBadge-badge--achieved' : ''"
								:href="badge.href"
							>
								<CloIcon :symbol="badge.icon" />
								<span class="ActivityBadge-description">{{ badge.text }}</span>
							</a>
						</li>
					</ul>
				</div>
				<div class="">
					<button class="Button Button--expanded" type="button" @click="close">
						{{ $t('generic.Close') }}
					</button>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
import CloButton from '@/base/CloButton/CloButton.vue';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloLoadingAnimation from '@/base/CloLoadingAnimation/CloLoadingAnimation.vue';
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
export default {
	components: { CloButton, CloHeading, CloLoadingAnimation, CloIcon },
	data() {
		return {
			active: false,
			clickedIndex: 0,
		};
	},
	props: {
		/**
		 * title of badges or species common name
		 */
		title: {
			type: String,
			defeault: null,
		},
		badges: {
			type: Array,
			required: true,
		},
		reverse: {
			type: Boolean,
			default: false,
		},
	},
	computed: {},
	methods: {
		open(index) {
			if (!this.active) {
				this.active = true;
				this.clickedIndex = index;
				this.$nextTick(() => {
					this.$refs.modal.focus();
				});
			} else {
				this.active = false;
			}
		},
		close() {
			this.active = false;
			this.$refs.badges[this.clickedIndex].focus();
		},
	},
};
</script>

<style lang="scss">
.ActivityBadge {
	.ActivityBadge-list {
		list-style: none;
		margin: 0;
		display: flex;
		li {
			@include inline-sm;
			&:last-child {
				margin: 0;
			}
		}
	}
	.ActivityBadge-badge {
		display: flex;
		flex-direction: column;
		align-items: center;
		color: $color-neutral-3;
		position: relative;
		.Icon {
			@include icon-lg;
			@include stack-xs;
		}
		.LoadingAnimation {
			.Icon {
				@include icon-sm;
				margin: $space-sm $space-sm 0.75rem $space-sm;
				fill: $color-neutral-3;
			}
		}
		&:hover + .ActivityBadge-tooltip {
			display: block;
		}
	}
	.ActivityBadge-label {
		display: none;
		font-size: $text-size-xs;
		text-transform: uppercase;
		@include breakpoint(medium) {
			display: inline;
		}
	}
	.ActivityBadge-badge--achieved {
		color: $color-link;
	}
	.ActivityBadge-tooltip {
		display: none;
		@include text(2);
		position: absolute;
		margin-top: $space-sm;
		background-color: $color-white;
		@include inset-squish-sm;
		@include dropshadow-2;
		border-radius: $radius-sm;
	}
	.ActivityBadge-modal {
		background-color: $color-primary-1;
		border-radius: 3px;
		@include dropshadow-2;
		@include inset-squish-lg;
		position: fixed;
		top: auto;
		bottom: 0;
		left: 0;
		right: 0;
		width: 100%;
		max-height: 100vh;
		transform: translate(0, 0);
		z-index: 1007;
		&.slideUp-enter-active,
		&.slideUp-leave-active {
			transition: transform 0.4s;
		}
		&.slideUp-enter,
		&.slideUp-leave-to {
			transform: translate(0, 100%);
		}
		display: flex;
		flex-direction: column;
	}
}

.ActivityBadge--reverse {
	.ActivityBadge-badge {
		color: $color-text-reverse;
	}
	.ActivityBadge-badge--achieved {
		color: $color-link-reverse;
	}
}

.ActivityBadge--vertical {
	.ActivityBadge-list {
		flex-direction: column;
		align-items: flex-start;
	}
	.ActivityBadge-badge {
		@include stack-md;
		flex-direction: row;
		align-items: center;
		.Icon {
			@include icon-md;
			@include inline-md;
		}
	}
}

// variant that hides the "seen this year" badge
.ActivityBadge--noYear {
	.ActivityBadge-list {
		li:last-child {
			display: none;
		}
	}
}
</style>
